import { useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, Clock3, ShieldCheck, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { waLink, WHATSAPP_DISPLAY } from "@/lib/whatsapp";

const SERVICES = ["Mutual Funds & SIP", "Fixed Income", "Term Insurance", "Not sure — need guidance"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: PhoneCall, text: "A real advisor calls you — not a bot" },
  { icon: Clock3, text: "Response within 24 hours, guaranteed" },
  { icon: ShieldCheck, text: "No spam, no pressure, no obligation" },
];

export const LeadSection = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [note, setNote] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/[\s-]/g, "").replace(/^\+91/, "");
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!service) {
      toast.error("Please select what you're interested in");
      return;
    }
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        phone: cleanPhone,
        service,
        note: note.trim(),
      });
    } catch {
      toast.warning("Could not save a backup copy, but WhatsApp will still open.");
    }
    let msg = `Hi Rajkiran Wealth, my name is ${name.trim()}. My phone number is ${cleanPhone}. I am interested in ${service}. Please call me back.`;
    if (note.trim()) msg += `\nNote: ${note.trim()}`;
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — send the message and we'll call you back within 24 hours.");
  };

  return (
    <section id="contact" className="py-20 lg:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-forest rounded-3xl overflow-hidden grid lg:grid-cols-2"
        >
          <div className="p-8 lg:p-16 text-bone">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze-light">Get started</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Get a free call back from Rajkiran
            </h2>
            <p className="mt-5 text-base text-bone/80 max-w-md">
              Share your details and they will reach us directly on WhatsApp at{" "}
              <span className="font-semibold text-bone">{WHATSAPP_DISPLAY}</span>. We'll call you
              back to understand your goals — free of charge.
            </p>
            <ul className="mt-10 space-y-5">
              {PERKS.map((p) => (
                <li key={p.text} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-bone/20">
                    <p.icon className="h-5 w-5 text-bronze-light" />
                  </span>
                  <span className="text-sm text-bone/90">{p.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 pt-8 border-t border-bone/15 space-y-3 text-sm text-bone/80">
              <a
                href="mailto:Jinendra0910@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 hover:text-bone transition-colors"
              >
                <Mail className="h-4 w-4 shrink-0 text-bronze-light" />
                Jinendra0910@gmail.com
              </a>
              <p className="flex items-start gap-3" data-testid="contact-office-delhi">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-bronze-light" />
                Unit 1701, 17th Floor, RG Trade Tower, Netaji Subhash Place, Pitampura, Delhi - 110034
              </p>
              <p className="flex items-start gap-3" data-testid="contact-office-tinsukia">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-bronze-light" />
                Manav Kalyan, Namghar Road, Tinsukia, Assam - 786125
              </p>
            </div>
          </div>

          <div className="p-6 lg:p-12 bg-forest-dark/40">
            <form
              onSubmit={handleSubmit}
              data-testid="lead-form"
              className="bg-white rounded-2xl p-6 lg:p-8 space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="lead-name">Full name</Label>
                <Input
                  id="lead-name"
                  data-testid="lead-name-input"
                  placeholder="e.g. Priya Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-phone">Mobile number</Label>
                <Input
                  id="lead-phone"
                  data-testid="lead-phone-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label>I'm interested in</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="lead-service-select" className="h-11">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`lead-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-note">Anything we should know? (optional)</Label>
                <Textarea
                  id="lead-note"
                  data-testid="lead-note-input"
                  placeholder="e.g. I want to start a SIP for my daughter's education"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </div>
              <Button
                type="submit"
                data-testid="lead-submit-btn"
                className="w-full h-12 bg-[#25D366] hover:bg-[#1eb85a] text-white font-semibold rounded-full transition-transform hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="h-5 w-5 mr-2" />
                Send via WhatsApp
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Your details open in WhatsApp on your device — nothing is stored on this website.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
